




















































































































































import { Component, Prop, Mixins, Inject } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { BrandConfigKeys } from "@/data/constants";

// Types
import { IState } from "@/store";
import { IProduct, IProductAutomationForm } from "@/models/products";
import { IProductProviderData } from "@/models/providers/products";
import { IBrand } from "@/models/brands";

@Component({
  components: {
    "suspend-interval-field": () => import("./suspendIntervalField.vue"),
    "cancel-interval-field": () => import("./cancelIntervalField.vue"),
    "close-interval-field": () => import("./closeIntervalField.vue"),
    "due-date-free-change-field": () => import("./dueDateFreeChangeField.vue"),
    "auto-create-renew-invoice-field": () =>
      import("./autoCreateRenewInvoiceField.vue"),
    "due-date-restriction-field": () => import("./dueDateRestrictionField.vue"),
    "reactivate-status-restriction-field": () =>
      import("./reactivateStatusRestrictionField.vue"),
    "product-cancel-anytime-checkbox": () =>
      import("./productCancelAnytimeCheckbox.vue"),
    "recommended-money-back-period-field": () =>
      import("./recommendedMoneyBackPeriodField.vue"),
    "invoice-generation-control": () =>
      import("@/components/app/global/invoices/invoiceGenerationControl.vue"),
    "recurring-due-date-control": () =>
      import("@/components/app/global/invoices/recurringDueDateControl.vue"),
    "invoice-consolidation-enabled-field": () =>
      import(
        "@/components/app/global/invoices/invoiceConsolidationEnabledField.vue"
      )
  },
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      },
      brandId(state: IState) {
        return state.brand.id;
      },
      brandScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](this.brandId);
      },
      brand(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BRANDS,
          scope: this.brandScope
        });
      }
    })
  }
})
export default class EditProductAutomation extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Getters

  public product!: IProduct;
  public productScope!: string;
  public brandId!: IBrand["id"];
  public brand!: IBrand;
  public brandScope!: string;

  // Inject

  @Inject({ from: "productProviderData" })
  public readonly $productProviderData!: () => IProductProviderData;

  // Props

  @Prop({ required: true })
  public productId!: IProduct["id"];

  // Data
  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public form = {} as IProductAutomationForm;
  public initialForm = {} as IProductAutomationForm;

  // Methods

  public async created() {
    if (!this.isContentAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([
        this.getProductData(this.isReloading),
        this.getBrandSettings(this.isReloading),
        this.getBrandDetails(this.isReloading)
      ]);

      this.initializeForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      ignoreStored,
      vm: this
    });
  }

  public getBrandSettings(ignoreStored = false) {
    return this.$store.dispatch("brand/getConfig", {
      brandId: this.brandId,
      keys: [
        BrandConfigKeys.SUBSCRIPTIONS_SUSPEND_INTERVAL,
        BrandConfigKeys.SUBSCRIPTIONS_CANCEL_INTERVAL,
        BrandConfigKeys.SUBSCRIPTIONS_CLOSE_INTERVAL,
        BrandConfigKeys.SUBSCRIPTIONS_MONEY_BACK_PERIOD
      ],
      ignoreStored
    });
  }

  public getBrandDetails(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/get`, {
      id: this.brandId,
      scope: this.brandScope,
      ignoreStored,
      vm: this
    });
  }

  public initializeForm() {
    this.form = _.pick(this.product, [
      "auto_create_renew_invoice",
      "cancel_anytime",
      "cancel_interval",
      "can_disable_auto_create_renew_invoice",
      "close_interval",
      "create_invoice_term",
      "due_date_free_change",
      "due_date_restriction",
      "invoice_consolidation_enabled",
      "payment_days_term",
      "reactivate_status_restriction",
      "recommended_money_back_period",
      "suspend_interval"
    ]);

    this.initialForm = _.cloneDeep(this.form);
  }

  public revertToInitialState() {
    this.form = _.cloneDeep(this.initialForm);
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      await this.updateProductData();
      await this.getProductData(true);

      this.initializeForm();

      this.$store.dispatch("toast/show", {
        message: this.$t(
          "_sentence.product_automation_settings_successfully_updated"
        )
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateProductData() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/update`, {
      id: this.productId,
      scope: this.productScope,
      data: this.newFormValues
    });
  }

  // Computed

  public get formIsDisabled() {
    return !this.canManageProduct || this.isProcessing || this.isStaged;
  }

  public get isFormChanged() {
    return !_.isEqual(this.form, this.initialForm);
  }

  public get newFormValues() {
    const newValues: any = _.omitBy(this.form, (v, k) => {
      return this.initialForm[k] === v;
    });
    return newValues;
  }

  public get $productData() {
    return this.$productProviderData();
  }

  public get isStaged() {
    return this.$productData.isStaged;
  }

  public get suspendIntervalBV() {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.SUBSCRIPTIONS_SUSPEND_INTERVAL
    ];
  }

  public get closeIntervalBV() {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.SUBSCRIPTIONS_CLOSE_INTERVAL
    ];
  }

  public get cancelIntervalBV() {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.SUBSCRIPTIONS_CANCEL_INTERVAL
    ];
  }

  public get moneyBackPeriodBV() {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.SUBSCRIPTIONS_MONEY_BACK_PERIOD
    ];
  }

  public get createInvoiceTermBV() {
    return _.get(this.brand, "create_invoice_term");
  }

  public get paymentDaysTermBV() {
    return _.get(this.brand, "payment_days_term");
  }

  public get canDisableAutoRenew() {
    return _.get(
      this.$productData,
      "product.can_disable_auto_create_renew_invoice",
      true
    );
  }

  // Guarding

  public guardFunctionalities = ["staff_get_product"];

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get canManageProduct() {
    return this.$userCan("update_product");
  }

  // Before destroy

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });

    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.BRANDS,
      scope: this.brandScope,
      vm: this
    });
  }
}
